import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';

const SuccessionSellerInfo = ({ query, updateCfaQuery, currentUser, intl }) => {
  const company = currentUser?.company;
  const isRJF = company?.host?.toString().includes("rjf.com");

  // Local state for percentage inputs to avoid reformatting during typing
  const [percentageInputs, setPercentageInputs] = useState({
    sellerGrowthRate: ((query.sellerFiveYearGrowthRate || 0) * 100).toFixed(1),
    sellerTaxRate: ((query.sellerTaxRate || 0) * 100).toFixed(1),
  });

  // Re-sync local state when query is loaded externally (e.g. pre-populated)
  useEffect(() => {
    setPercentageInputs({
      sellerGrowthRate: ((query.sellerFiveYearGrowthRate || 0) * 100).toFixed(1),
      sellerTaxRate: ((query.sellerTaxRate || 0) * 100).toFixed(1),
    });
  }, [query.id]);

  const handlePercentageChange = (key, displayValue, field) => {
    setPercentageInputs(prev => ({ ...prev, [key]: displayValue }));
    const numValue = parseFloat(displayValue);
    if (!isNaN(numValue)) {
      updateCfaQuery({ ...query, [field]: numValue / 100 });
    }
  };

  const validate = () => {
    let valid = true;

    if (!query.sellerT12Revenue || parseFloat(query.sellerT12Revenue) < 1) {
      valid = false;
    }

    if (query.sellerManualTaxRate == undefined || (query.sellerManualTaxRate && parseFloat(query.sellerTaxRate || 0) < 0.01)) {
      valid = false;
    }

    if (query.valid !== valid) {
      updateCfaQuery({ ...query, valid });
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue);
  };

  const handleCurrencyInput = (value, field) => {
    // Remove currency formatting and keep only numbers and decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');
    updateCfaQuery({ 
      ...query, 
      [field]: parseFloat(numericValue) || 0 
    });
  };

  // Auto-set tax rate for RJF
  useEffect(() => {
    if (isRJF) {
      updateCfaQuery({ ...query, sellerManualTaxRate: false });
    }
  }, [isRJF]);

  // Run validation on mount and when query changes
  useEffect(() => {
    validate();
  }, [query.sellerT12Revenue, query.sellerManualTaxRate, query.sellerTaxRate]);

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">
        <FormattedMessage id="calculators.cfa.tell_us_about" />
      </h2>
      
      <p className="text-gray-600 mb-8">
        <FormattedMessage id="calculators.cfa.participant_copy" />
      </p>

      <div className="space-y-6">
        {/* Seller T12 Revenue */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <FormattedMessage id="calculators.cfa.seller_t12_rev_q" />
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
            <input
              type="text"
              value={query.sellerT12Revenue || ''}
              onChange={(e) => handleCurrencyInput(e.target.value, 'sellerT12Revenue')}
              className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-right"
              placeholder="0"
            />
          </div>
        </div>

        {/* Five Year Growth Rate (hidden for RJF) */}
        {!isRJF && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FormattedMessage id="calculators.cfa.seller_5_growth_q" />
            </label>
            <div className="relative">
              <input
                type="number"
                value={percentageInputs.sellerGrowthRate}
                onChange={(e) => handlePercentageChange('sellerGrowthRate', e.target.value, 'sellerFiveYearGrowthRate')}
                onFocus={(e) => e.target.select()}
                className="w-full pr-8 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="7.0"
                min="-100"
                step="0.1"
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
            </div>
          </div>
        )}

        {/* Tax Rate Override (hidden for RJF) */}
        {!isRJF && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FormattedMessage id="calculators.cfa.manually_override_tax_rate" />
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="false"
                  checked={query.sellerManualTaxRate === false}
                  onChange={() => updateCfaQuery({ ...query, sellerManualTaxRate: false })}
                  className="mr-2 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-700">
                  {intl.formatMessage({ id: "calculators.cfa.dynamic" })}
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="true"
                  checked={query.sellerManualTaxRate === true}
                  onChange={() => updateCfaQuery({ 
                    ...query, 
                    sellerManualTaxRate: true,
                    sellerTaxRate: query.sellerTaxRate || 0.35
                  })}
                  className="mr-2 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-700">
                  {intl.formatMessage({ id: "calculators.cfa.manual" })}
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Manual Tax Rate Input */}
        {query.sellerManualTaxRate && !isRJF && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FormattedMessage id="calculators.cfa.tax_rate" />
            </label>
            <div className="relative">
              <input
                type="number"
                value={percentageInputs.sellerTaxRate}
                onChange={(e) => handlePercentageChange('sellerTaxRate', e.target.value, 'sellerTaxRate')}
                onFocus={(e) => e.target.select()}
                className="w-full pr-8 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                placeholder="35.0"
                min="0"
                max="300"
                step="0.1"
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
            </div>
          </div>
        )}
      </div>

      {/* Validation Status */}
      {!query.valid && (
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-800 text-sm">
            {intl.formatMessage({ id: 'succession.cash_flow_analysis.seller_info.complete_required_fields' })}
          </p>
        </div>
      )}
    </div>
  );
};

export default injectIntl(SuccessionSellerInfo); 